/**
 * HierarchyChip — Spec 287: mobile header affordance showing the active
 * Hierarchy's name (<768px only, rendered by PillarLayout between the
 * wordmark and the avatar button).
 *
 * - 2+ Hierarchies: tappable pill + chevron, opens HierarchySwitcherSheet.
 * - Exactly 1 Hierarchy: static label, no chevron, not tappable (Flow C).
 * - 0 Hierarchies / orgs not yet loaded: renders nothing — never show a
 *   stale/wrong name (Spec 287 Empty/Loading states table).
 */
import React from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Button } from '../ui/Button';

interface HierarchyChipProps {
  /** Whether the switcher sheet this chip controls is currently open. */
  open: boolean;
  /** Opens the switcher sheet. Only invoked when 2+ Hierarchies exist. */
  onOpen: () => void;
}

function ChevronDownIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-text-secondary"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

const HierarchyChip: React.FC<HierarchyChipProps> = ({ open, onOpen }) => {
  const { organization, organizations, organizationsLoaded } = useOrganization();

  // Loading/empty: hide entirely rather than render a wrong/stale name; also
  // covers the 0-Hierarchy case (owned by NoHierarchyRedirectBanner elsewhere).
  if (!organizationsLoaded || organizations.length === 0 || !organization) {
    return null;
  }

  const isMulti = organizations.length > 1;

  if (!isMulti) {
    // Flow C: single Hierarchy — plain text, no pill/border/chevron, not a button.
    return (
      <span
        className="min-w-0 max-w-[140px] truncate text-[13px] font-semibold text-text-secondary"
        title={organization.name}
        data-testid="hierarchy-chip-static"
      >
        {organization.name}
      </span>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      className="p-0 h-11 min-w-touch bg-transparent hover:bg-transparent active:bg-transparent border-0 justify-start shrink-0"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Current Hierarchy: ${organization.name}. Switch Hierarchy`}
      data-testid="hierarchy-chip"
      onClick={onOpen}
    >
      <span className="inline-flex items-center gap-1 min-w-0 max-w-[140px] h-8 pl-2.5 pr-2 rounded-full bg-[var(--theme-bg-tertiary)] border border-border-light">
        <span className="truncate text-[13px] font-semibold text-[var(--theme-primary)]">
          {organization.name}
        </span>
        <ChevronDownIcon />
      </span>
    </Button>
  );
};

export default HierarchyChip;
